import { Inject, Injectable, Optional } from '@angular/core';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Entsorger } from './entsorger/entsorger';
import { MatDialog } from '@angular/material/dialog';
import { Haendler } from './haendler/haendler';
import { HttpParams } from '@angular/common/http';
import { Tierreklamation } from '../gruppe-tier/classes/tierreklamation';
import { Client } from '../client/classes/client';
import { KieneApi, KieneBackendApiService } from '../../services/backend-api/kiene-backend-api.service';
import { API_BASE_URL_SERVICE } from '../../kiene-core.config';
import { EnterValueInput, EnterValueResponse } from '../dialogs/enter-value-dialog/enter-value-response';
import { EnterValueDialogComponent } from '../dialogs/enter-value-dialog/enter-value-dialog.component';
import { MessageService } from '../../services/message-service/message.service';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { KieneUtils } from '../../utils/KieneUtils';
import { UserClient } from './benutzer/benutzer';
import { ConfirmDialogInput, ConfirmDialogResult } from '../dialogs/confirm-dialog/confirm-dialog';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { Futtermittellieferant } from './lieferant/lieferant';
import { Mandant } from '../mandanten/classes/mandant';
import { ExternTierarzt } from './extern-tierarzt/extern-tierarzt';

@Injectable({ providedIn: 'root' })
export class StammdatenService {
    setLetzterAngelegterHaendler: Haendler;
    setLetzterAngelegterEntsorger: Entsorger;
    setLetzterAngelegterLieferant: Futtermittellieferant;

    haendlerFehlt = false;
    entsorgerFehlt = false;
    lieferantFehlt = false;
    haendlerFehltSubject: Subject<boolean> = new Subject<boolean>();
    entsorgerFehltSubject: Subject<boolean> = new Subject<boolean>();
    lieferantFehltSubject: Subject<boolean> = new Subject<boolean>();

    ausgewaehlteVvvoSubject: Subject<Client> = new Subject<Client>();
    vvvoAktualisiertSubject: Subject<Client> = new Subject<Client>();

    constructor(
        @Optional() private readonly dialog: MatDialog,
        private readonly api: KieneBackendApiService,
        private readonly localStorageService: LocalStorageService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly messageService: MessageService,
    ) {}

    setHaendlerFehlt(b: boolean) {
        this.haendlerFehlt = b;
        this.haendlerFehltSubject.next(this.haendlerFehlt);
    }

    getHaendlerFehlt() {
        return this.haendlerFehlt;
    }

    watchHaendlerFehlt(): Observable<boolean> {
        return this.haendlerFehltSubject.asObservable();
    }

    getEntsorgerFehlt() {
        return this.entsorgerFehlt;
    }

    watchEntsorgerFehlt(): Observable<boolean> {
        return this.entsorgerFehltSubject.asObservable();
    }

    getLieferantFehlt() {
        return this.lieferantFehlt;
    }

    watchAusgewaehlteVvvo(): Observable<Client> {
        return this.ausgewaehlteVvvoSubject.asObservable();
    }

    setAusgewaehlteVvvo(client: Client) {
        this.localStorageService.setCurrentClient(client, 'StammdatenService');
        this.ausgewaehlteVvvoSubject.next(client);
    }

    getAusgewaehlteVvvo(): Client {
        return this.localStorageService.getCurrentClient();
    }

    watchAktualisierteVvvo(): Observable<Client> {
        return this.vvvoAktualisiertSubject.asObservable();
    }

    setAktualisierteVvvo(client: Client) {
        this.vvvoAktualisiertSubject.next(client);
    }

    ladeFuttermittellieferantenFavoriten(): Observable<any> {
        return this.api.getAll(`${this.apiBaseUrl}futtermittellieferant/read_favoriten.php`);
    }

    ladeVvvos(search?: string, limit?: number, freigaben?: number, currentKundeId?: number): Observable<KieneApi> {
        let params = new HttpParams();
        if (search) {
            params = params.set('search', search);
        }
        if (limit > 0) {
            params = params.set('limit', limit);
        }
        if (freigaben !== null && freigaben !== undefined) {
            params = params.set('freigaben', freigaben);
        }
        if (currentKundeId) {
            params = params.set('current_kunde_id', currentKundeId);
        }
        return this.api.getAll(`${this.apiBaseUrl}kunde/read_kundenportal.php`, params);
    }

    /** Volle Betriebsliste. Ohne dieses Flag liefert der Endpunkt nur die ersten 100. */
    ladeAlleVvvos(): Observable<KieneApi> {
        const params = new HttpParams().set('alle', '1');
        return this.api.getAll(`${this.apiBaseUrl}kunde/read_kundenportal.php`, params);
    }

    istBetriebInKundenportal(kundeId: number): Observable<boolean> {
        const id = Number(kundeId);
        if (!id) {
            return of(false);
        }
        const params = new HttpParams().set('kunde_id', String(id)).set('limit', '1');
        return this.api.getAll(`${this.apiBaseUrl}kunde/read_kundenportal.php`, params).pipe(
            map(res => (res?.records || []).some(client => Number(client?.kunde_id) === id)),
            catchError(() => of(false)),
        );
    }

    ladeMeineVvvos(search?: string, limit?: number): Observable<KieneApi> {
        let params = new HttpParams();
        if (search) {
            params = params.set('search', search);
        }
        if (limit > 0) {
            params = params.set('limit', limit);
        }
        return this.api.getAll(`${this.apiBaseUrl}kunde/read_kundenportal_meine.php`, params);
    }

    ladeFreigegebeneVvvos(currentKundeId?: number, search?: string, limit?: number): Observable<KieneApi> {
        let params = new HttpParams();
        if (search) {
            params = params.set('search', search);
        }
        if (limit > 0) {
            params = params.set('limit', limit);
        }
        if (currentKundeId) {
            params = params.set('current_kunde_id', currentKundeId);
        }
        return this.api.getAll(`${this.apiBaseUrl}kunde/read_kundenportal_freigaben.php`, params);
    }

    /** Meine Betriebe plus freigegebene Betriebe (dedupliziert by kunde_id). */
    ladeMeineUndFreigegebeneVvvos(search?: string, limit?: number, currentKundeId?: number): Observable<KieneApi> {
        const empty: KieneApi = { records: [], count: 0 };
        return forkJoin({
            meine: this.ladeMeineVvvos(search, limit).pipe(catchError(() => of(empty))),
            freigaben: this.ladeFreigegebeneVvvos(currentKundeId, search, limit).pipe(catchError(() => of(empty))),
        }).pipe(
            map(({ meine, freigaben }) => {
                const byKundeId = new Map<number, Client>();
                for (const client of [...(meine?.records || []), ...(freigaben?.records || [])]) {
                    const kundeId = Number(client?.kunde_id);
                    if (kundeId > 0 && !byKundeId.has(kundeId) && this.isAktiverUmstallBetrieb(client)) {
                        byKundeId.set(kundeId, client);
                    }
                }
                const records = Array.from(byKundeId.values()).sort((a, b) =>
                    String(a?.bezeichnung_final || a?.bezeichnung || '').localeCompare(
                        String(b?.bezeichnung_final || b?.bezeichnung || ''),
                        'de',
                    ),
                );
                return {
                    records,
                    count: records.length,
                    recordcount: records.length,
                } as KieneApi;
            }),
        );
    }

    /**
     * Zielbetriebe fuer Umstallen: eigene + freigegebene Betriebe des Gruppen-Inhabers
     * (Landwirt-Konto), z. B. fuer Tierarzt/Admin im Umstallen-Dialog.
     */
    ladeZielbetriebeFuerInhaber(
        inhaberKundeId: number,
        search?: string,
        limit?: number,
    ): Observable<KieneApi> {
        let params = new HttpParams().set('inhaber_kunde_id', String(inhaberKundeId));
        if (search) {
            params = params.set('search', search);
        }
        if (limit > 0) {
            params = params.set('limit', limit);
        }
        return this.api.getAll(`${this.apiBaseUrl}kunde/read_kundenportal_zielbetriebe.php`, params).pipe(
            map(resp => this.filterAktiveUmstallBetriebe(resp)),
        );
    }

    /** Inaktive Betriebe (be.inaktiv) und inaktive Kundenkonten (km.aktiv=0) ausblenden. */
    private isAktiverUmstallBetrieb(client: Client | null | undefined): boolean {
        if (!client) {
            return false;
        }
        if (Number(client.inaktiv) > 0) {
            return false;
        }
        if (KieneUtils.isApiZero(client.aktiv)) {
            return false;
        }
        return true;
    }

    private filterAktiveUmstallBetriebe(resp: KieneApi): KieneApi {
        const records = (resp?.records || []).filter(c => this.isAktiverUmstallBetrieb(c));
        return {
            ...resp,
            records,
            count: records.length,
            recordcount: records.length,
        } as KieneApi;
    }

    /** Zielbetriebe für Umstallen (Landwirt/Viehhändler oder Inhaber-Konto). */
    loadZielbetriebeForUmstallen(
        inhaberKundeId?: number | null,
        search?: string,
        limit?: number,
    ): Observable<KieneApi> {
        if (this.localStorageService.isCurrentUserKunde() || this.localStorageService.isCurrentUserViehhaendler()) {
            return this.ladeMeineUndFreigegebeneVvvos(search, limit);
        }
        const inhaberId = Number(inhaberKundeId);
        if (inhaberId > 0) {
            return this.ladeZielbetriebeFuerInhaber(inhaberId, search, limit);
        }
        return this.ladeMeineUndFreigegebeneVvvos(search, limit);
    }

    ladeZugewieseneKunden(benutzerId: number) {
        return this.api.getAll(this.apiBaseUrl + 'kunde/read_benutzer.php?benutzer_id=' + benutzerId);
    }

    kundeBenutzerZuweisen(benutzerId: number, kundeId: number) {
        const userClient: UserClient = new UserClient();
        userClient.benutzer_id = benutzerId;
        userClient.kunde_id = kundeId;
        return this.api.post(this.apiBaseUrl + 'benutzer_kunde/create.php', userClient);
    }

    kundeBenutzerEntfernen(benutzerId: number, kundeId: number) {
        const obj = { benutzer_id: benutzerId, kunde_id: kundeId };
        return this.api.post(this.apiBaseUrl + 'benutzer_kunde/delete.php', obj);
    }

    loadEntsorgerFavoriten(): Observable<KieneApi> {
        let params = new HttpParams().set('favorit', '1');
        params = params.set('orderby', 'letzte_entsorgung desc');
        return this.api.getAll(`${this.apiBaseUrl}entsorger/read_kundenportal.php`, params);
    }

    loadHaendlerFavoriten(search?: string): Observable<KieneApi> {
        let params = new HttpParams().set('favorit', '1');
        if (search) {
            params = params.set('search', search);
        }
        return this.api.getAll(`${this.apiBaseUrl}viehhaendler/read_kundenportal.php`, params);
    }

    deleteHaendlerFavorit(haendler: Haendler) {
        return this.api.deleteGeneric(`${this.apiBaseUrl}viehhaendler/delete_kundenportal.php`, haendler);
    }

    deleteEntsorgerFavorit(entsorger: Entsorger) {
        entsorger.favorit = 0;
        return this.api.updateGeneric(`${this.apiBaseUrl}entsorger/favorit.php`, entsorger);
    }

    loadHaendler(): Observable<any> {
        return this.api.getAll(`${this.apiBaseUrl}viehhaendler/read_kundenportal.php`);
    }

    updateEntsorger(entsorger: Entsorger) {
        return this.api.updateGeneric(`${this.apiBaseUrl}entsorger/update_kundenportal.php`, entsorger);
    }

    createHaendler(haendler: Haendler) {
        return this.api.post(`${this.apiBaseUrl}viehhaendler/create_kundenportal.php`, haendler, null);
    }

    updateHaendler(haendler: Haendler) {
        return this.api.updateGeneric(`${this.apiBaseUrl}viehhaendler/update_kundenportal.php`, haendler);
    }

    createOrUpdateFuttermittellieferant(futtermittellieferant: Futtermittellieferant) {
        const currentKundeId = this.localStorageService.getCurrentClient()?.kunde_id;
        const payload: Futtermittellieferant & { kunde_id?: number } = {
            ...futtermittellieferant,
            ...(currentKundeId ? { kunde_id: Number(currentKundeId) } : {}),
        };

        if (futtermittellieferant?.futtermittellieferant_id) {
            return this.api.updateGeneric(`${this.apiBaseUrl}futtermittellieferant/update_kundenportal.php`, payload);
        }
        return this.api.post(`${this.apiBaseUrl}futtermittellieferant/create_kundenportal.php`, payload, null);
    }

    setFuttermittellieferantFavorit(kundeId: number, futtermittellieferantId?: number) {
        const obj = {
            kunde_id: kundeId,
            futtermittellieferant_id: futtermittellieferantId,
        };
        return this.api.post(`${this.apiBaseUrl}futtermittellieferant/favorit.php`, obj, null);
    }

    ladeMeineExternTieraerzte(): Observable<KieneApi> {
        return this.api.getAll(`${this.apiBaseUrl}extern_tierarzt/read_meine.php`);
    }

    createOrUpdateExternTierarzt(tierarzt: ExternTierarzt): Observable<ExternTierarzt> {
        return this.api.post(`${this.apiBaseUrl}extern_tierarzt/create_kundenportal.php`, tierarzt, null);
    }

    getExternTierarzt(externTierarztId: number): Observable<ExternTierarzt> {
        const params = new HttpParams().set('extern_tierarzt_id', externTierarztId);
        return this.api.get(`${this.apiBaseUrl}extern_tierarzt/read_one.php`, params);
    }

    assignExternTierarztToKunde(kundeId: number, externTierarztId?: number): Observable<unknown> {
        return this.api.post(
            `${this.apiBaseUrl}extern_tierarzt/assign.php`,
            { kunde_id: kundeId, extern_tierarzt_id: externTierarztId ?? null },
            null,
        );
    }

    unlinkExternTierarztFromKunde(kunde: Client): Observable<boolean> {
        const subject = new Subject<boolean>();
        const input = new ConfirmDialogInput();
        input.headline = 'Tierarzt entfernen';
        input.text =
            'Möchten Sie die Zuordnung des Tierarzten zu diesem Betrieb wirklich entfernen? Der Tierarzt bleibt in Ihrer Liste erhalten.';
        input.okButton = 'Entfernen';
        input.buttonColor = 'warn';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    const kundeId = Number(kunde?.kunde_id || 0);
                    if (!kundeId) {
                        subject.next(false);
                        return;
                    }
                    this.assignExternTierarztToKunde(kundeId).subscribe({
                        next: () => {
                            this.messageService.infoMessage('Tierarzt wurde vom Betrieb entfernt.');
                            subject.next(true);
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler!', err);
                            subject.next(false);
                        },
                    });
                } else {
                    subject.next(false);
                }
            },
        });
        return subject.asObservable();
    }
    deleteFuttermittellieferant(futtermittellieferantId: number) {
        return this.api.deleteGeneric(`${this.apiBaseUrl}futtermittellieferant/delete_kundenportal.php`, {
            futtermittellieferant_id: futtermittellieferantId,
        });
    }
    getEntsorger(id: any): Observable<any> {
        return this.api.getByIdAlternative(`${this.apiBaseUrl}entsorger/read_one_kundenportal.php`, 'entsorger_id', id);
    }

    getHaendler(id: any): Observable<any> {
        return this.api.getByIdAlternative(
            `${this.apiBaseUrl}viehhaendler/read_one_kundenportal.php`,
            'viehhaendler_id',
            id,
        );
    }

    getFuttermittellieferant(futtermittellieferant_id: number): Observable<any> {
        let params = new HttpParams();
        params = params.set('futtermittellieferant_id', futtermittellieferant_id);
        return this.api.get(`${this.apiBaseUrl}futtermittellieferant/read_one_kundenportal.php`, params);
    }

    getKunde(kunde_id: any) {
        return this.api.getByIdAlternative(`${this.apiBaseUrl}kunde/read_one_kundenportal.php`, 'kunde_id', kunde_id);
    }

    searchKundeByVvvo(vvvo: string): Observable<KieneApi> {
        const params = new HttpParams().set('vvvo', vvvo);
        return this.api.getAll(`${this.apiBaseUrl}kunde/search_vvvo_kundenportal.php`, params);
    }

    saveKunde(kunde: Client): Observable<Client> {
        return this.api.updateGeneric(`${this.apiBaseUrl}kunde/update_kundenportal.php`, kunde);
    }

    /**
     * speichert einen neuen Haendler im Service
     * @param haendler
     */
    speichereLetzenAngelegtenHaendler(haendler: Haendler) {
        this.setLetzterAngelegterHaendler = haendler;
    }

    /**
     * speichert einen neuen Haendler im Service
     * @param lieferant
     */
    speichereLetzenAngelegtenFuttermittellieferant(futtermittellieferant: Futtermittellieferant) {
        this.setLetzterAngelegterLieferant = futtermittellieferant;
    }

    /**
     * speichert einen neuen Entsorger im Service
     * @param entsorger
     */
    speichereLetzenAngelegtenEntsorger(entsorger: Entsorger) {
        this.setLetzterAngelegterEntsorger = entsorger;
    }

    /**
     * Liefert den letzten angelegten Entsorger zurück und löscht den Wert aus dem Service
     */
    ladeLetztenAngelegtenEntsorger() {
        const entsorger = this.setLetzterAngelegterEntsorger;
        this.setLetzterAngelegterEntsorger = null;
        return entsorger;
    }

    hitPinAendern(kundeId: number, pin: string): Observable<any> {
        return this.api.post(
            `${this.apiBaseUrl}kunde/update_hit_pin_kundenportal.php`,
            { kunde_id: kundeId, pin: pin },
            null,
        );
    }

    // Tierreklamationen

    updateTierreklamation(tierreklamation: Tierreklamation): Observable<any> {
        return this.api.updateGeneric(`${this.apiBaseUrl}tierreklamation/update_kundenportal.php`, tierreklamation);
    }

    getTierreklamation(id: number): Observable<any> {
        return this.api.getByIdAlternative(
            `${this.apiBaseUrl}tierreklamation/read_one_kundenportal.php`,
            'tierreklamation_id',
            id.toString(),
        );
    }

    reklamationenTeilen() {
        const input = new EnterValueInput();
        input.title = 'Reklamationen freigeben?';
        input.text =
            'Die Email-Adresse des Benutzers angeben, für den der lesende Zugriff auf die Reklamationen erlaubt werden soll.';
        input.okButton = 'freigeben';
        input.buttonColor = 'accent';
        input.addInputValue('Email Adresse', 'text');

        const dialogRef = this.dialog.open(EnterValueDialogComponent, { data: input, position: { top: '10px' } });
        dialogRef.afterClosed().subscribe({
            next: (res: EnterValueResponse) => {
                if (res?.result === true) {
                    this.api
                        .post(this.apiBaseUrl + 'tierreklamation/teilen.php', { email: res.getValue(0) }, null)
                        .subscribe({
                            next: () => {
                                this.messageService.infoMessage('Die Reklamationen wurden erfolgreich freigegeben!');
                            },
                            error: err => {
                                this.messageService.errorMessage('Fehler bei Freigabe!', err);
                            },
                        });
                }
            },
        });
    }

    addTierreklamation(reklamation: string): Observable<any> {
        const tierreklamation = new Tierreklamation();
        tierreklamation.reklamation = reklamation;
        return this.updateTierreklamation(tierreklamation);
    }

    deleteTierreklamation(tierreklamation: Tierreklamation): Observable<any> {
        return this.api.deleteGeneric(`${this.apiBaseUrl}tierreklamation/delete_kundenportal.php`, tierreklamation);
    }

    getTierreklamationen(inklFreigegebene: boolean): Observable<any> {
        const params = new HttpParams().set('freigegeben', inklFreigegebene ? '1' : '0');
        return this.api.getAll(`${this.apiBaseUrl}tierreklamation/read_kundenportal.php`, params);
    }

    loadBetriebsarten(): Observable<any> {
        return this.api.getAll(`${this.apiBaseUrl}betriebsart/read_kundenportal.php`);
    }

    ladeAbteile(gruppe_id: number) {
        const params = new HttpParams().set('gruppe_id', gruppe_id?.toString());
        return this.api.getAll(`${this.apiBaseUrl}gruppe_tier/read_gruppe_abteil.php`, params);
    }

    ladeHerkunftsbetriebe(gruppe_id: number) {
        const params = new HttpParams().set('gruppe_id', gruppe_id?.toString());
        return this.api.getAll(`${this.apiBaseUrl}gruppe_tier/read_gruppe_herkunft.php`, params);
    }

    ladeTierreklamationen(gruppe_id: number) {
        const params = new HttpParams().set('gruppe_id', gruppe_id.toString());
        return this.api.getAll(`${this.apiBaseUrl}gruppe_tier/read_gruppe_reklamation.php`, params);
    }

    ladeGeschlechter(gruppe_id: number) {
        const params = new HttpParams().set('gruppe_id', gruppe_id.toString());
        return this.api.getAll(`${this.apiBaseUrl}gruppe_tier/read_gruppe_geschlecht.php`, params);
    }

    ladeRassen(gruppe_id: number) {
        const params = new HttpParams().set('gruppe_id', gruppe_id.toString());
        return this.api.getAll(`${this.apiBaseUrl}gruppe_tier/read_gruppe_rasse.php`, params);
    }

    ladeTranspondernummern(gruppe_id: number) {
        const params = new HttpParams().set('gruppe_id', gruppe_id.toString());
        return this.api.getAll(`${this.apiBaseUrl}gruppe_tier/read_gruppe_transpondernummer.php`, params);
    }

    ladeViehhaendler(gruppe_id: number, params?: HttpParams) {
        if (!params) {
            params = new HttpParams();
        }
        params = params.set('gruppe_id', gruppe_id.toString());
        return this.api.getAll(`${this.apiBaseUrl}gruppe_tier/read_gruppe_viehhaendler.php`, params);
    }

    ladeBetriebskontakteFuerGruppe(gruppe_id: number, params?: HttpParams) {
        if (!params) {
            params = new HttpParams();
        }
        params = params.set('gruppe_id', gruppe_id.toString());
        return this.api.getAll(`${this.apiBaseUrl}gruppe_tier/read_gruppe_betriebskontakte.php`, params);
    }

    ladeEinstalldaten(gruppe_id: number, params) {
        if (!params) {
            params = new HttpParams();
        }
        params = params.set('gruppe_id', gruppe_id.toString());
        return this.api.getAll(`${this.apiBaseUrl}gruppe_tier/read_gruppe_einstalldatum.php`, params);
    }

    freigabeTierarztpraxis(kunde: Client) {
        const input = new EnterValueInput();
        input.title = 'Tierarztpraxis hinzufügen';
        input.text =
            'Tierarztpraxis als betreuende Praxis hinzufügen, sodass diese Belege und Dokumentationen etc. in der Software für diesen Betrieb vornehmen kann.';
        input.okButton = 'Hinzufügen';
        input.buttonColor = 'accent';
        input.addInputValue('VVVO der Praxis (12-stellig): 276...', 'text');

        this.dialog
            .open(EnterValueDialogComponent, { data: input, disableClose: true })
            .afterClosed()
            .subscribe({
                next: (res: EnterValueResponse) => {
                    if (res?.result === true) {
                        this.praxisVvvoSuchen(kunde, res.getValue(0));
                    }
                },
            });
    }

    private praxisVvvoSuchen(kunde: Client, vvvo: string) {
        this.api.getByIdAlternative(this.apiBaseUrl + 'mandant/suche.php', 'vvvo', vvvo).subscribe({
            next: (mandant: Mandant) => {
                const input = new ConfirmDialogInput();
                input.headline = 'Bestätigung';
                input.text = 'Möchten Sie "' + mandant.bezeichnung + '" als betreuende Tierarztpraxis hinzufügen?';
                input.okButton = 'Bestätigen';

                this.dialog
                    .open(ConfirmDialogComponent, { data: input })
                    .afterClosed()
                    .subscribe({
                        next: (response: ConfirmDialogResult) => {
                            if (response?.confirmed) {
                                this.praxisFreigabeErteilen(kunde, mandant);
                            }
                        },
                    });
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
            },
        });
    }

    private praxisFreigabeErteilen(kunde: Client, mandant: Mandant) {
        this.api
            .post(this.apiBaseUrl + 'kunde_mandant/freigabe.php', {
                kunde_id: kunde?.kunde_id,
                mandant_id: mandant?.mandant_id,
            })
            .subscribe({
                next: () => {
                    this.messageService.infoMessage('Tierarztpraxis erfolgreich hinzugefügt!');
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                },
            });
    }

    removeVVVO(kunde: Client, isFreigegebenerBetrieb = false): Observable<boolean> {
        const subject = new Subject<boolean>();
        const input = new ConfirmDialogInput();
        input.headline = isFreigegebenerBetrieb ? 'Freigabe entfernen' : 'VVVO entfernen';
        input.text = isFreigegebenerBetrieb
            ? 'Möchten Sie den freigegebenen Betrieb ' +
              kunde?.vvvo +
              ' aus Ihrer Liste entfernen? Sie verlieren den Zugriff auf diesen Betrieb.'
            : 'Wollen Sie wirklich die VVVO ' +
              kunde?.vvvo +
              ' aus Ihrer Benutzerkonfiguration entfernen? Sie können jederzeit die VVVVO wieder hinzufügen!';
        input.okButton = 'Entfernen';
        input.buttonColor = 'warn';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    const bk = {
                        kunde_id: kunde?.kunde_id,
                        benutzer_id: this.localStorageService.getCurrentUser().benutzer_id,
                        freigabe_entziehen: isFreigegebenerBetrieb ? 1 : 0,
                    };
                    this.api.deleteGeneric(this.apiBaseUrl + 'benutzer_kunde/delete_kundenportal.php', bk).subscribe({
                        next: () => {
                            this.messageService.infoMessage(
                                isFreigegebenerBetrieb
                                    ? 'Die Freigabe wurde erfolgreich entfernt!'
                                    : 'Die VVVO wurde erfolgreich entfernt!',
                            );
                            subject.next(true);
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler!', err);
                            subject.next(false);
                        },
                    });
                } else {
                    subject.next(false);
                }
            },
        });
        return subject;
    }
}
